/**
 * WebCrypto HMAC-SHA256 for the browser scrub pass — the one-way protected-ID
 * transform that lets Loan/Tax IDs leave the browser without their raw values.
 *
 * A protected ID is `base64url(HMAC-SHA256(tenantKey, canonicalizeId(rawValue)))`
 * (PRODUCT.md "Protected ID"). The canonicalization + base64url codec live in the
 * isomorphic `common/uploader/protected-id.ts`; THIS module is the browser-only
 * WebCrypto half: importing the raw tenant key as a non-extractable `CryptoKey`
 * and signing canonicalized values with it.
 *
 * Two security properties this module establishes:
 *
 *   1. **Non-extractable key.** The key is imported with `extractable: false`, so
 *      even injected script can only ever call `sign` with it — the raw 32 bytes
 *      can never be read back out of the `CryptoKey` (`exportKey` rejects).
 *      Defense in depth around the in-memory-only key.
 *   2. **One-way + deterministic.** HMAC-SHA256 is non-invertible without the key
 *      (which the backend holds only encrypted), and `canonicalizeId` guarantees
 *      the same logical ID maps to the same protected ID across snapshots, so the
 *      backend can join on protected IDs while never seeing a raw value.
 *
 * Browser-only: uses the WebCrypto `crypto.subtle` global. Reuses the /common
 * helpers (`canonicalizeId`, `base64UrlEncode/Decode`) — never reimplements them.
 */

import { canonicalizeId, base64UrlEncode, base64UrlDecode } from '../../common/uploader';
import type { ProtectedIdKind } from '../../common/uploader';

/** The HMAC algorithm both `importHmacKey` and `computeProtectedId` use. */
const HMAC_PARAMS = { name: 'HMAC', hash: 'SHA-256' } as const;

/**
 * Import a per-tenant raw key (base64url of 32 bytes) as a non-extractable
 * HMAC-SHA256 `CryptoKey`. `extractable: false` is the load-bearing argument: the
 * raw bytes can never be exported from the returned key — only `sign` is allowed.
 *
 * @throws If `base64UrlKey` is not valid base64url (surfaced by the codec).
 */
export async function importHmacKey(base64UrlKey: string): Promise<CryptoKey> {
  const raw = base64UrlDecode(base64UrlKey);
  return crypto.subtle.importKey(
    'raw',
    // Pass the typed-array VIEW (a valid `BufferSource`), not a bare
    // `ArrayBuffer`: a `Uint8Array` view satisfies WebCrypto in browsers and in
    // the jsdom test realm, whereas a cross-realm `ArrayBuffer` fails jsdom's
    // strict `instanceof` check. `asArrayBufferView` re-anchors the bytes on a
    // fresh `ArrayBuffer` so the view's type is `Uint8Array<ArrayBuffer>` (the
    // shape `BufferSource` requires; `base64UrlDecode`'s return widens to
    // `ArrayBufferLike`).
    asArrayBufferView(raw),
    HMAC_PARAMS,
    /* extractable */ false,
    ['sign'],
  );
}

/** Copy bytes into a fresh `ArrayBuffer`-backed `Uint8Array` view. */
function asArrayBufferView(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  const view = new Uint8Array(new ArrayBuffer(bytes.byteLength));
  view.set(bytes);
  return view;
}

const ENCODER = new TextEncoder();

/**
 * Compute the deterministic one-way protected ID for a raw value:
 * `base64url(HMAC-SHA256(key, canonicalizeId(rawValue, kind)))`.
 *
 * Determinism comes from `canonicalizeId` (the same logical ID canonicalizes
 * identically for a given `kind`), so two snapshots of the same loan produce the
 * same protected ID.
 *
 * A blank ID is NOT an ID. A raw value that canonicalizes to the empty string
 * (blank / whitespace-only / sentinel) returns the empty string rather than a
 * protected ID — HMAC-of-empty is one constant value, so emitting it would make
 * every blank-Loan-ID row collide on a single phantom loan once the backend joins
 * by protected ID (PRODUCT.md "Loan identity": a row without a usable Loan ID is
 * retained as source data but is not merged into a loan). The backend treats an
 * empty `loan_id` cell as blank — the row is kept and reported under
 * `rowsWithoutLoanId`, never merged.
 */
export async function computeProtectedId(
  key: CryptoKey,
  rawValue: string,
  kind: ProtectedIdKind,
): Promise<string> {
  const canonical = canonicalizeId(rawValue, kind);
  // No usable identifier → emit a blank cell, never a protected ID for "nothing"
  // (which would be one join-collidable constant across all blank rows).
  if (canonical === '') return '';
  const signature = await crypto.subtle.sign(HMAC_PARAMS, key, ENCODER.encode(canonical));
  return base64UrlEncode(new Uint8Array(signature));
}
